import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { useChats } from '@/data/store';
import { useOverlay } from '@/components/Overlay';
import { Avatar } from '@/components/Avatar';
import { BackIcon } from '@/components/Icons';
import { approvedStarters, findPeople, previewText, startChat, startOptions, starterTitle, typedNumber, type Starter, type Who } from '@/lib/newChat';
import type { Brand } from '@/data/types';
import { useAuth } from '@/lib/auth';

// New chat (Gary, 15 Oct), as WhatsApp's pencil: find the person, then pick an approved
// message. WhatsApp only lets a business start a chat with a Meta-approved message; once the
// customer replies, the chat is like any other. Nothing sends until "Send" is tapped.
// A number nobody has in Sentinel can be messaged too (Gary, 16 Oct): their first name is
// typed, only the introductions fit, and the server makes them a lead.
// People on both teams choose the number first (Peace of Sleep, 3 Nov 2026); it shows once that number is connected.

/** The Peace of Sleep messages that fit a number nobody has in Sentinel. */
const POS_INTROS = ['pos_intro_after_call', 'pos_intro_referral'];

export function NewChatScreen() {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { live, chats } = useChats();
  const { toast } = useOverlay();
  const auth = useAuth();
  const myFirst = auth.profile?.first_name?.trim().split(' ')[0] || 'me';
  const canCmb = auth.access.brands.includes('cmb');
  const canPos = auth.access.brands.includes('pos');
  const [posReady, setPosReady] = useState(false);
  const [term, setTerm] = useState('');
  const [people, setPeople] = useState<Who[] | null>(null);
  // From a chat whose 24 hours are up (Gary, 15 Oct): that person is already chosen.
  const restart = useSearchParams()[0].get('chat');
  const restartChat = restart ? chats.find((c) => c.id === restart) : undefined;
  const [who, setWho] = useState<Who | null>(() => (restartChat
    ? { kind: 'chat', id: restartChat.id, name: restartChat.name, firstName: restartChat.name.split(' ')[0] ?? '', phone: null }
    : null));
  const [brand, setBrand] = useState<Brand>(() => restartChat?.brand ?? (canCmb ? 'cmb' : 'pos'));
  const [starters, setStarters] = useState<Starter[] | null>(null);
  const [pick, setPick] = useState<Starter | null>(null);
  const [details, setDetails] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [openWhen, setOpenWhen] = useState<string | null>(null);

  useEffect(() => {
    if (live && canPos) void startOptions().then((o) => setPosReady(o.pos));
  }, [live, canPos]);

  // A restarted chat keeps its own number, also when the chat list arrives after this screen opened.
  const restartBrand = restartChat?.brand;
  useEffect(() => {
    if (restartBrand && restartBrand !== brand) { setBrand(restartBrand); setStarters(null); }
  }, [restartBrand, brand]);

  // Search as they type, a moment after the last key.
  useEffect(() => {
    if (!live || term.trim().length < 2) { setPeople(null); return; }
    const t = window.setTimeout(() => { void findPeople(term, brand).then(setPeople); }, 300);
    return () => window.clearTimeout(t);
  }, [term, live, brand]);

  useEffect(() => {
    if (who && !starters) void approvedStarters(brand).then(setStarters);
  }, [who, starters, brand]);

  function chooseBrand(b: Brand) {
    if (b === brand) return;
    setBrand(b);
    setPeople(null);
    setStarters(null);
  }

  // The new chat opens once it has arrived in the list.
  useEffect(() => {
    if (openWhen && chats.some((c) => c.id === openWhen)) navigate(`/chat/${openWhen}`, { replace: true });
  }, [openWhen, chats, navigate]);

  async function send() {
    if (!who || !pick || busy) return;
    setBusy(true);
    const r = await startChat(who, pick.name, details.map((d) => d.trim()), brand);
    setBusy(false);
    if ('error' in r) { toast(r.error); return; }
    toast(`Sent to ${who.firstName.trim() || who.name}`);
    if (restart) { goBack(`/chat/${restart}`); return; }
    setOpenWhen(r.id);
  }

  const isNumber = who?.kind === 'number';
  const ready = !!pick && details.length === pick.places.length && details.every((d) => d.trim())
    && (!isNumber || !!who?.firstName.trim());
  // A typed number that none of the results has: offer to message it as it is.
  const digits = typedNumber(term);
  const offerNumber = !!digits && people !== null && !people.some((p) => (p.phone ?? '').replace(/\D/g, '').endsWith(digits.slice(-9)));
  const shown = (s: Starter) => (!isNumber ? true : brand === 'pos' ? POS_INTROS.includes(s.name) : s.sender);
  // Peace of Sleep waits for its number (switch day); a restarted POS chat has it already.
  const posWaiting = brand === 'pos' && !posReady && !restart;
  const pos = brand === 'pos';
  const values = (extra: string[]) => [who?.firstName.trim() || 'there', ...(pick?.sender ?? false ? [myFirst] : []), ...extra];
  const valuesFor = (s: Starter) => [who?.firstName.trim() || 'there', ...(s.sender ? [myFirst] : [])];

  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => (pick ? setPick(null) : restart ? goBack(`/chat/${restart}`) : who ? setWho(null) : goBack('/'))}><BackIcon /></button>
        <h1 className="chats-topbar-title">{who ? `Message ${who.firstName.trim() || who.name}` : 'New chat'}</h1>
      </header>
      <main className="chats-screen chats-screen--grey">
        {!live && <p className="chats-help-intro">Sample data: starting a chat works on live chats only.</p>}

        {live && !who && !restart && canCmb && canPos && posReady && (
          <div className="chats-chips" role="group" aria-label="Send from">
            <button type="button" className={`chats-chip${brand === 'cmb' ? ' active' : ''}`} onClick={() => chooseBrand('cmb')}>CleanMyBed</button>
            <button type="button" className={`chats-chip${brand === 'pos' ? ' active' : ''}`} onClick={() => chooseBrand('pos')}>Peace of Sleep</button>
          </div>
        )}

        {live && posWaiting && <p className="chats-help-intro">The Peace of Sleep WhatsApp number is not connected yet.</p>}

        {live && !who && !posWaiting && (
          <>
            <div className="chats-pad">
              <input className="chats-input" autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder={pos ? 'Peace of Sleep client: name or number' : 'Customer or lead: name or number'} aria-label={pos ? 'Find a Peace of Sleep client' : 'Find a customer or lead'} />
            </div>
            <p className="chats-help-intro">WhatsApp lets a business start a chat only with a message Meta has approved. Pick the person, then the message.</p>
            {people !== null && (
              <div className="chats-group">
                {people.length === 0 && !offerNumber && <div className="chats-set"><div className="chats-set-main"><small>Nobody found.</small></div></div>}
                {offerNumber && (
                  <button type="button" className="chats-set" onClick={() => { setWho({ kind: 'number', id: '', name: term.trim(), firstName: '', phone: term.trim() }); setPick(null); }}>
                    <Avatar name="#" colour={0} small />
                    <div className="chats-set-main">Message {term.trim()}<small>{pos ? 'Not a Peace of Sleep client yet' : 'Not in Sentinel yet: they become your lead'}</small></div>
                  </button>
                )}
                {people.map((p) => (
                  <button key={`${p.kind}-${p.id}`} type="button" className="chats-set" onClick={() => { setWho(p); setPick(null); }}>
                    <Avatar name={p.name} colour={p.name.length} small />
                    <div className="chats-set-main">{p.name}<small>{p.kind === 'customer' ? (pos ? 'Client' : 'Customer') : 'Lead'}{p.phone ? ` · ${p.phone}` : ' · no number'}</small></div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {live && who && !pick && (
          <>
            {isNumber && (
              <>
                <p className="chats-group-title">Their first name</p>
                <div className="chats-pad">
                  <input
                    className="chats-input"
                    autoFocus
                    maxLength={30}
                    value={who.firstName}
                    onChange={(e) => setWho({ ...who, firstName: e.target.value })}
                    placeholder="First name"
                    aria-label="Their first name"
                  />
                </div>
              </>
            )}
            <p className="chats-group-title">Choose the message</p>
            <div className="chats-group">
              {starters === null && <div className="chats-set"><div className="chats-set-main"><small>Loading...</small></div></div>}
              {starters?.length === 0 && <div className="chats-set"><div className="chats-set-main"><small>Meta has not approved any chat starters yet.</small></div></div>}
              {starters?.filter(shown).map((s) => (
                <button
                  key={s.name}
                  type="button"
                  className="chats-set"
                  onClick={() => {
                    if (isNumber && !who.firstName.trim()) { toast('Type their first name first'); return; }
                    setPick(s);
                    setDetails(s.places.map(() => ''));
                  }}
                >
                  <div className="chats-set-main">{starterTitle(s.name)}<small>{previewText(s.body, valuesFor(s))}</small></div>
                </button>
              ))}
            </div>
          </>
        )}

        {live && who && pick && (
          <>
            {pick.places.length > 0 && (
              <>
                <p className="chats-group-title">Details</p>
                <div className="chats-pad">
                  {pick.places.map((label, i) => (
                    <input
                      key={label}
                      className="chats-input chats-input--stack"
                      value={details[i] ?? ''}
                      maxLength={60}
                      onChange={(e) => setDetails((d) => d.map((v, j) => (j === i ? e.target.value : v)))}
                      placeholder={label}
                      aria-label={label}
                    />
                  ))}
                </div>
              </>
            )}
            <p className="chats-group-title">{who.firstName.trim() || who.name} will see</p>
            <div className="chats-pad">
              <div className="chats-msg chats-msg--out chats-newchat-preview">{previewText(pick.body, values(details.map((d) => d.trim())))}</div>
            </div>
            <div className="chats-pad">
              <button type="button" className="chats-btn" disabled={!ready || busy || !!openWhen} onClick={() => void send()}>
                {busy ? 'Sending...' : openWhen ? 'Opening the chat...' : `Send to ${who.firstName.trim() || who.name}`}
              </button>
            </div>
          </>
        )}
      </main>
    </>
  );
}
